/**
 * features/audit/AuditPanel.tsx
 *
 * Orchestration root. Tab routing + records view only.
 * All sub-views extracted to components/.
 */

import React, { useState, useMemo } from 'react';
import { useStore } from '../../store';
import { AuditRecord, AuditMetrics } from '../../types';
import { computeAuditMetrics } from '../../services/auditService';

// ── Extracted components ─────────────────────────────────────────────────────
import { Th, Td, tbStyle }    from './components/AuditTablePrimitives';
import { MetricsCards }       from './components/MetricsCards';
import { AuditExportActions } from './components/AuditExportActions';
import { PerformanceView }    from './components/PerformanceView';
import { IntelligenceView }   from './components/IntelligenceView';
import { AdaptiveEdgeView }   from './components/AdaptiveEdgeView';
import { TradePlansView }     from './components/TradePlansView';
import { WaitLogView }        from './components/WaitLogView';
import { CalibrationView }    from './components/CalibrationView';
import { colorR, r2 }         from './utils/formatters';

// ─── Outcome editor ───────────────────────────────────────────────────────────
// Kept here: shares editId state with records table via closure.

function OutcomeEditor({ record, onSave, onClose }: {
  record: AuditRecord;
  onSave: (r: AuditRecord) => void;
  onClose: () => void;
}) {
  const [exit,   setExit]   = useState(record.exitPrice?.toString() ?? '');
  const [status, setStatus] = useState(record.status);
  const [notes,  setNotes]  = useState(record.notes);
  const [mfe,    setMfe]    = useState(record.mfe?.toString() ?? '');
  const [mae,    setMae]    = useState(record.mae?.toString() ?? '');

  const save = () => {
    const exitPrice = exit ? parseFloat(exit) : null;
    const entry     = record.entryPrice;
    const stop      = record.stopPrice;
    const riskPerR  = entry && stop ? Math.abs(entry - stop) : null;
    const realizedR = exitPrice !== null && entry !== null && riskPerR && riskPerR > 0
      ? (record.direction === 'long' ? exitPrice - entry : entry - exitPrice) / riskPerR
      : null;
    const outcome: AuditRecord['outcome'] =
      status === 'skipped'     ? 'skipped'
      : status === 'invalidated' ? 'invalidated'
      : realizedR === null       ? 'open'
      : realizedR > 0.05         ? 'win'
      : realizedR < -0.05        ? 'loss'
      : 'breakeven';
    onSave({ ...record, status, outcome, exitPrice,
      realizedR: realizedR !== null ? +realizedR.toFixed(2) : null,
      mfe: mfe ? +parseFloat(mfe).toFixed(2) : null,
      mae: mae ? +parseFloat(mae).toFixed(2) : null,
      notes });
    onClose();
  };

  const inputStyle = { background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 4, color: 'var(--t1)', fontSize: 11, padding: '4px 8px',
    fontFamily: 'var(--mono)', width: '100%' };

  return (
    <div style={{ padding: 12, background: 'var(--surface2)', borderRadius: 6,
      border: '1px solid var(--cyan)44', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--cyan)' }}>
        Update: {record.symbol} {record.setupType} ({record.direction})
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        <div>
          <div style={{ fontSize: 9, color: 'var(--t3)', marginBottom: 3 }}>STATUS</div>
          <select value={status} onChange={e => setStatus(e.target.value as AuditRecord['status'])} style={inputStyle}>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
            <option value="skipped">Skipped</option>
            <option value="invalidated">Invalidated</option>
          </select>
        </div>
        <div>
          <div style={{ fontSize: 9, color: 'var(--t3)', marginBottom: 3 }}>EXIT PRICE</div>
          <input type="number" value={exit} onChange={e => setExit(e.target.value)} placeholder="e.g. 185.50" style={inputStyle} />
        </div>
        <div>
          <div style={{ fontSize: 9, color: 'var(--t3)', marginBottom: 3 }}>MFE (R) / MAE (R)</div>
          <div style={{ display: 'flex', gap: 4 }}>
            <input type="number" value={mfe} onChange={e => setMfe(e.target.value)} placeholder="MFE" style={{ ...inputStyle, width: '50%' }} />
            <input type="number" value={mae} onChange={e => setMae(e.target.value)} placeholder="MAE" style={{ ...inputStyle, width: '50%' }} />
          </div>
        </div>
      </div>
      <textarea value={notes} onChange={e => setNotes(e.target.value)}
        placeholder="Notes..." rows={2}
        style={{ ...inputStyle, resize: 'vertical', fontFamily: 'var(--sans)', fontSize: 11 }} />
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{ padding: '4px 12px', borderRadius: 4, cursor: 'pointer',
          background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--t2)', fontSize: 11 }}>
          Cancel
        </button>
        <button onClick={save} style={{ padding: '4px 12px', borderRadius: 4, cursor: 'pointer',
          background: 'var(--cyan)20', border: '1px solid var(--cyan)', color: 'var(--cyan)', fontSize: 11, fontWeight: 700 }}>
          Save
        </button>
      </div>
    </div>
  );
}

// ─── Main Panel ───────────────────────────────────────────────────────────────

export function AuditPanel() {
  const {
    auditRecords, updateAuditRecord, deleteAuditRecord, clearAuditRecords,
    tradePlans, updateTradePlan, deleteTradePlan,
    waitRecords, updateWaitRecord, deleteWaitRecord,
  } = useStore();

  const [view,        setView]        = useState<'records' | 'metrics' | 'plans' | 'wait' | 'performance' | 'intelligence' | 'adaptive' | 'calibration'>('records');
  const [editId,      setEditId]      = useState<string | null>(null);
  const [filterSym,   setFilterSym]   = useState('');
  const [filterStatus,setFilterStatus]= useState('');

  const metrics: AuditMetrics = useMemo(() =>
    computeAuditMetrics(auditRecords), [auditRecords]);

  const filtered = useMemo(() => auditRecords
    .filter(r => !filterSym    || r.symbol.toLowerCase().includes(filterSym.toLowerCase()))
    .filter(r => !filterStatus || r.status === filterStatus)
    .sort((a, b) => b.createdAt - a.createdAt),
    [auditRecords, filterSym, filterStatus]
  );

  const btnStyle = (active: boolean) => ({
    padding: '4px 14px', borderRadius: 4, cursor: 'pointer', fontSize: 11,
    fontFamily: 'var(--mono)', fontWeight: active ? 700 : 400,
    background: active ? 'var(--cyan)20' : 'var(--surface)',
    border: `1px solid ${active ? 'var(--cyan)' : 'var(--border)'}`,
    color: active ? 'var(--cyan)' : 'var(--t2)',
  });

  return (
    <div style={{ padding: '0 16px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 8 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <button style={btnStyle(view === 'records')}     onClick={() => setView('records')}>Records</button>
          <button style={btnStyle(view === 'metrics')}     onClick={() => setView('metrics')}>Metrics</button>
          <button style={btnStyle(view === 'performance')} onClick={() => setView('performance')}>Performance</button>
          <button style={btnStyle(view === 'plans')}       onClick={() => setView('plans')}>
            Trade Plans {tradePlans.length > 0 ? `(${tradePlans.length})` : ''}
          </button>
          <button style={btnStyle(view === 'wait')}        onClick={() => setView('wait')}>
            Wait Log {waitRecords.length > 0 ? `(${waitRecords.length})` : ''}
          </button>
          <button style={btnStyle(view === 'intelligence')} onClick={() => setView('intelligence')}>⚡ Intelligence</button>
          <button style={btnStyle(view === 'adaptive')}    onClick={() => setView('adaptive')}>🧠 Adaptive Edge</button>
          <button style={btnStyle(view === 'calibration')} onClick={() => setView('calibration')}>📊 Calibration</button>
        </div>
        <AuditExportActions
          auditRecords={auditRecords}
          onClearAll={clearAuditRecords}
          btnStyle={btnStyle}
        />
      </div>

      {/* ── RECORDS VIEW ─────────────────────────────────────────────────── */}
      {view === 'records' && (
        <>
          {!auditRecords.length ? (
            <div style={{ padding: '24px 0', color: 'var(--t3)', textAlign: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6, color: 'var(--t2)' }}>No audit records yet</div>
              <div style={{ fontSize: 11, maxWidth: 400, margin: '0 auto' }}>
                Setups are automatically logged when you click <strong>⬇ Save Snapshot</strong> in the
                AI Thesis panel. Update outcomes here after trades close.
              </div>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={filterSym} onChange={e => setFilterSym(e.target.value)}
                  placeholder="Filter symbol…"
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 4,
                    color: 'var(--t1)', fontSize: 11, padding: '4px 8px', fontFamily: 'var(--mono)', flex: 1 }} />
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 4,
                    color: 'var(--t1)', fontSize: 11, padding: '4px 8px', fontFamily: 'var(--mono)' }}>
                  <option value="">All statuses</option>
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                  <option value="skipped">Skipped</option>
                  <option value="invalidated">Invalidated</option>
                </select>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={tbStyle}>
                  <thead>
                    <tr>
                      <Th>Symbol</Th><Th>Setup</Th><Th>Dir</Th><Th>Phase</Th>
                      <Th>Trend</Th><Th right>Conf</Th><Th>Readiness</Th>
                      <Th>Status</Th><Th>Outcome</Th><Th right>R</Th><Th>Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(r => (
                      <React.Fragment key={r.id}>
                        <tr>
                          <Td><span style={{ fontWeight: 700 }}>{r.symbol}</span></Td>
                          <Td><span style={{ fontSize: 10 }}>{r.setupType.substring(0, 20)}</span></Td>
                          <Td color={r.direction === 'long' ? 'var(--green)' : 'var(--red)'}>{r.direction}</Td>
                          <Td><span style={{ fontSize: 10 }}>{r.phase}</span></Td>
                          <Td color={r.trendAlignment === 'aligned_with_trend' ? 'var(--green)' : r.trendAlignment === 'counter_trend' ? 'var(--red)' : 'var(--t3)'}>
                            <span style={{ fontSize: 10 }}>{r.trendAlignment === 'aligned_with_trend' ? '↑ align' : r.trendAlignment === 'counter_trend' ? '↓ counter' : '⟷ neutral'}</span>
                          </Td>
                          <Td right>{r.confidence}</Td>
                          <Td><span style={{ fontSize: 10, color: r.readiness === 'urgent' ? 'var(--gold)' : r.readiness === 'ready' ? 'var(--green)' : 'var(--t2)' }}>{r.readiness}</span></Td>
                          <Td><span style={{ fontSize: 10 }}>{r.status}</span></Td>
                          <Td color={r.outcome === 'win' ? 'var(--green)' : r.outcome === 'loss' ? 'var(--red)' : 'var(--t3)'}>
                            {r.outcome}
                          </Td>
                          <Td right color={colorR(r.realizedR)}>{r2(r.realizedR)}</Td>
                          <Td>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button onClick={() => setEditId(editId === r.id ? null : r.id)}
                                style={{ padding: '2px 8px', borderRadius: 3, cursor: 'pointer', fontSize: 10,
                                  background: 'var(--cyan)10', border: '1px solid var(--cyan)44', color: 'var(--cyan)' }}>
                                {editId === r.id ? 'Close' : 'Edit'}
                              </button>
                              <button onClick={() => deleteAuditRecord(r.id)}
                                style={{ padding: '2px 8px', borderRadius: 3, cursor: 'pointer', fontSize: 10,
                                  background: 'var(--red)10', border: '1px solid var(--red)44', color: 'var(--red)' }}>
                                ✕
                              </button>
                            </div>
                          </Td>
                        </tr>
                        {editId === r.id && (
                          <tr>
                            <td colSpan={11} style={{ padding: '6px 10px' }}>
                              <OutcomeEditor record={r} onSave={updateAuditRecord} onClose={() => setEditId(null)} />
                            </td>
                          </tr>
                        )}
                        {r.notes && (
                          <tr>
                            <td colSpan={11} style={{ padding: '2px 10px 6px', fontSize: 10, color: 'var(--t3)', fontStyle: 'italic' }}>
                              {r.notes}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {/* ── METRICS VIEW ─────────────────────────────────────────────────── */}
      {view === 'metrics' && (
        !auditRecords.length
          ? <div style={{ padding: '24px 0', color: 'var(--t3)', textAlign: 'center', fontSize: 11 }}>Metrics populate once you have audit records with closed outcomes.</div>
          : <MetricsCards metrics={metrics} auditCount={auditRecords.length} />
      )}

      {view === 'performance'  && <PerformanceView auditRecords={auditRecords} />}
      {view === 'intelligence' && <IntelligenceView auditRecords={auditRecords} />}
      {view === 'adaptive'     && <AdaptiveEdgeView auditRecords={auditRecords} tradePlans={tradePlans} />}
      {view === 'plans'        && <TradePlansView plans={tradePlans} auditRecords={auditRecords} onUpdate={updateTradePlan} onDelete={deleteTradePlan} />}
      {view === 'wait'         && <WaitLogView records={waitRecords} onUpdate={updateWaitRecord} onDelete={deleteWaitRecord} />}
      {view === 'calibration'  && <CalibrationView auditRecords={auditRecords} />}

    </div>
  );
}

export const COMPLIANCE_DISCLOSURE =
  'This system provides market analysis and structured trade planning tools. ' +
  'It does not provide investment advice, does not execute trades, ' +
  'and does not connect to any brokerage platform.';
